export interface LandingV3Content {
  seo: {
    title: string
    description: string
  }
  hero: {
    badge: string
    title: string
    body: string
    primaryCta: string
    secondaryCta: string
    caption: string
  }
  context: {
    kicker: string
    title: string
    lead: string
    body: string
    actionKicker: string
    actionTitle: string
  }
  morning: {
    kicker: string
    title: string
    body: string
    notes: Array<{ title: string; body: string }>
    overlayLabel: string
    overlayTitle: string
    overlayBody: string
  }
  action: {
    kicker: string
    title: string
    body: string
    detailKicker: string
    detailTitle: string
    detailBody: string
    steps: Array<{ title: string; body: string }>
  }
  authority: {
    kicker: string
    title: string
    body: string
    notes: Array<{ title: string; body: string }>
  }
  memory: {
    kicker: string
    title: string
    body: string
    steps: Array<{ title: string; body: string }>
  }
  how: {
    title: string
    body: string
    steps: Array<{ title: string; body: string }>
  }
  cta: {
    title: string
    body: string
    button: string
  }
}
const getCopy = (
  marketingCopy: Record<string, string>,
  key: string,
  fallback: string,
): string => marketingCopy[key]?.trim() || fallback

/**
 * Resolves the full landing-page narrative in one place.
 *
 * The server publishes the same `landing_v3_*` defaults through
 * `/billing/plans`. These local values keep the public page useful when the
 * pricing endpoint is unavailable; configured values always win.
 */
export function resolveLandingV3Content(
  marketingCopy: Record<string, string>,
): LandingV3Content {
  const copy = (key: string, fallback: string) => getCopy(marketingCopy, key, fallback)

  return {
    seo: {
      title: copy('landing_v3_seo_title', 'Ricald | AI Commercial Operations for B2B Teams'),
      description: copy(
        'landing_v3_seo_description',
        'Ricald coordinates B2B commercial work from its native pipeline or your existing CRM, plus Ads, email, calendar, website, and relationship context—then acts within your rules.',
      ),
    },
    hero: {
      // The badge is where a buyer works out what aisle they are in, so it uses
      // the category they already search for. The differentiated framing still
      // does its job further down the page, where it earns the premium instead
      // of asking the reader to learn a new noun before they know what this is.
      badge: copy('landing_v3_hero_badge', 'The AI commercial operator for B2B teams'),
      title: copy('landing_v3_hero_title', 'Your commercial operation, continuously moving.'),
      body: copy(
        'landing_v3_hero_body',
        'Give Ricald the outcome. It works from shared commercial memory, coordinates the next moves within your authority, and brings your team only the decisions that require judgment.',
      ),
      primaryCta: copy('landing_v3_hero_primary_cta', 'See Ricald at work'),
      secondaryCta: copy('landing_v3_hero_secondary_cta', 'Explore the operating model'),
      caption: copy(
        'landing_v3_hero_caption',
        'Work progresses · Exceptions find you · Outcomes improve what happens next',
      ),
    },
    context: {
      kicker: copy('landing_v3_context_kicker', 'The context gap'),
      // Plain statement of the pain first. "Demand is visible. The next action
      // is not." is the sharper line, but it only lands once the reader knows
      // which gap is being described, so it is promoted directly beneath rather
      // than asked to open the section cold.
      title: copy('landing_v3_context_title', 'Demand is visible. The next action is not.'),
      lead: copy('landing_v3_context_lead', 'Demand is visible. The next action is not.'),
      body: copy(
        'landing_v3_context_body',
        'Commercial context arrives continuously, then fragments across systems with different owners, histories, and definitions of what matters.',
      ),
      actionKicker: copy('landing_v3_context_action_kicker', 'Ricald preserves the thread'),
      // "Explainable" is kept deliberately over "coordinated". Coordination is
      // what every orchestration tool claims; explainability is the claim the
      // evidence row directly beneath actually demonstrates, and it is the one a
      // competitor cannot copy without building the receipt trail underneath it.
      actionTitle: copy(
        'landing_v3_context_action_title',
        'Scattered context becomes one explainable action.',
      ),
    },
    morning: {
      kicker: copy('landing_v3_morning_kicker', 'A different start to the day'),
      // Was "Open Ricald after the work already has shape." — poetic, and it
      // made the reader work out what it meant. This names the trade directly:
      // the thing you stop doing, and the thing you do instead.
      title: copy('landing_v3_morning_title', 'Start your day with decisions, not reconstruction.'),
      body: copy(
        'landing_v3_morning_body',
        'Don’t spend your morning piecing together yesterday. Ricald has already connected the latest context and pushed routine work forward.',
      ),
      notes: [
        {
          title: copy('landing_v3_morning_note_1_title', 'What changed since yesterday'),
          body: copy(
            'landing_v3_morning_note_1_body',
            'The small GTM events that materially changed the situation.',
          ),
        },
        {
          title: copy('landing_v3_morning_note_2_title', 'What Ricald handled'),
          body: copy(
            'landing_v3_morning_note_2_body',
            'Research, qualification, and approved actions completed inside policy.',
          ),
        },
        {
          title: copy('landing_v3_morning_note_3_title', 'What needs judgment'),
          body: copy(
            'landing_v3_morning_note_3_body',
            'Only the decisions whose consequence, uncertainty, or authority requires you.',
          ),
        },
      ],
      overlayLabel: copy('landing_v3_morning_overlay_label', 'One decision for you'),
      overlayTitle: copy(
        'landing_v3_morning_overlay_title',
        'Northstar annual offer is ready for approval.',
      ),
      overlayBody: copy(
        'landing_v3_morning_overlay_body',
        'Evidence, expected effect, and the authority boundary are attached.',
      ),
    },
    action: {
      // "From objective to action" described a transition rather than saying
      // what the section is about. The distinct line is the title beneath it, so
      // the eyebrow now just sets it up.
      kicker: copy('landing_v3_action_kicker', 'Every recommendation'),
      title: copy('landing_v3_action_title', 'The next move arrives with its reason.'),
      body: copy(
        'landing_v3_action_body',
        'Ricald does not stop at surfacing an account. It prepares the action, preserves the evidence, and pauses only where judgment belongs to you.',
      ),
      detailKicker: copy('landing_v3_action_detail_kicker', 'One explainable next move'),
      detailTitle: copy('landing_v3_action_detail_title', 'See why. Decide once. Keep moving.'),
      detailBody: copy(
        'landing_v3_action_detail_body',
        'It arrives with what changed, who is involved, the relationship context, how confident it is, and the authority it needs.',
      ),
      steps: [
        {
          title: copy('landing_v3_action_step_1_title', 'Situation assembled'),
          body: copy(
            'landing_v3_action_step_1_body',
            'What changed, what remains unresolved, and why it matters now.',
          ),
        },
        {
          title: copy('landing_v3_action_step_2_title', 'Action prepared'),
          body: copy(
            'landing_v3_action_step_2_body',
            'The strongest route, message, and timing are ready—not left as suggestions.',
          ),
        },
        {
          title: copy('landing_v3_action_step_3_title', 'Authority respected'),
          body: copy(
            'landing_v3_action_step_3_body',
            'Routine work proceeds. Consequential decisions pause with evidence attached.',
          ),
        },
      ],
    },
    authority: {
      kicker: copy('landing_v3_authority_kicker', 'Authority designed in'),
      title: copy('landing_v3_authority_title', 'You set the rules. Ricald runs the loop.'),
      body: copy(
        'landing_v3_authority_body',
        'Define who Ricald can reach, in what voice, on which terms, and at which confidence. It works inside those boundaries and records every action in an audit trail.',
      ),
      notes: [
        {
          title: copy('landing_v3_authority_note_1_title', 'Routine work continues'),
          body: copy(
            'landing_v3_authority_note_1_body',
            'Research, qualification, and approved follow-up do not wait for another click.',
          ),
        },
        {
          title: copy('landing_v3_authority_note_2_title', 'Exceptions find you'),
          body: copy(
            'landing_v3_authority_note_2_body',
            'Risk, uncertainty, or authority boundaries determine when judgment is required.',
          ),
        },
        {
          title: copy('landing_v3_authority_note_3_title', 'Autonomy is earned'),
          // Accurate as shipped, verified 2026-08-05. The loop is live via the
          // PlatformPattern factor in agents/decisions/confidence.py: weight 0.20
          // in _DEFAULT_WEIGHTS, valued on outcome-derived quality/wilson_lower,
          // and ADDITIVE — ADR-0027 notes a low-quality match can LOWER composed
          // confidence, never only amplify it. Composed-vs-floor is what decides
          // whether an action proceeds or pauses, so outcomes really do widen and
          // narrow what runs. (The tenant's OWN prior is separate and still at
          // weight 0 until P6; that one is not the mechanism behind this line.)
          body: copy(
            'landing_v3_authority_note_3_body',
            'Outcomes strengthen or contract what Ricald may handle next time.',
          ),
        },
      ],
    },
    memory: {
      // The title was already the key message; the eyebrow above it was the
      // buzzword. "Shared memory" now appears as the mechanism inside the body,
      // where it explains the claim instead of announcing itself.
      kicker: copy('landing_v3_memory_kicker', 'What carries forward'),
      title: copy('landing_v3_memory_title', 'Every outcome changes what happens next.'),
      body: copy(
        'landing_v3_memory_body',
        'Shared memory is how. Ricald carries forward accounts, relationships, commitments, and outcomes, so each objective starts from what your team already learned.',
      ),
      steps: [
        {
          title: copy('landing_v3_memory_step_1_title', 'The objective persists'),
          body: copy(
            'landing_v3_memory_step_1_body',
            'Work stays attached to the outcome it serves, not to a temporary session.',
          ),
        },
        {
          title: copy('landing_v3_memory_step_2_title', 'Outcomes stay connected'),
          body: copy(
            'landing_v3_memory_step_2_body',
            'Replies, meetings, progression, and closed results connect to the decision that produced them.',
          ),
        },
        {
          title: copy('landing_v3_memory_step_3_title', 'The next cycle improves'),
          body: copy(
            'landing_v3_memory_step_3_body',
            'Targeting, timing, language, and authority update from what actually happened.',
          ),
        },
      ],
    },
    how: {
      title: copy('landing_v3_how_title', 'How your day gets easier'),
      body: copy('landing_v3_how_body', 'Three short steps. Then Ricald keeps the thread.'),
      steps: [
        {
          title: copy('landing_v3_how_step_1_title', 'Choose your source of truth'),
          body: copy(
            'landing_v3_how_step_1_body',
            'Use Ricald’s native pipeline or keep your existing CRM, then connect Ads, email, calendar, website, and relationship context.',
          ),
        },
        {
          title: copy('landing_v3_how_step_2_title', 'Set the authority once'),
          body: copy(
            'landing_v3_how_step_2_body',
            'Define the voice, confidence, and consequence boundaries. Ricald decides from there.',
          ),
        },
        {
          title: copy('landing_v3_how_step_3_title', 'Check decisions and outcomes'),
          body: copy(
            'landing_v3_how_step_3_body',
            'Step in where judgment matters. Let routine work continue without coordination overhead.',
          ),
        },
      ],
    },
    cta: {
      title: copy(
        'landing_v3_cta_title',
        'Give Ricald the outcome. Keep the judgment that matters.',
      ),
      body: copy(
        'landing_v3_cta_body',
        'See what changes when GTM work can observe, decide, act, and learn continuously.',
      ),
      button: copy('landing_v3_cta_button', 'Start with one objective'),
    },
  }
}
